import { ArrowUp, Bot, MessageCircle, X } from "lucide-react"
import type { FormEvent } from "react"
import { useEffect, useRef, useState } from "react"

type Message = { id: number; role: "assistant" | "user"; text: string }

const prompts = [
  "What problem does Resolto solve?",
  "What can I try in the demo?",
  "How are actions controlled?",
  "Which connectors are simulated?",
]

const answer = (question: string) => {
  const value = question.toLowerCase()

  if (value.includes("plan") || value.includes("price") || value.includes("15")) {
    return "The source outline positions early design-partner pilots at $500–$1,000 per month plus $1,000–$3,000 setup. Public self-serve pricing is not represented as live in this demo."
  }

  if (value.includes("ai") || value.includes("control")) {
    return "AI may classify, summarise, and recommend, but it cannot execute an unrestricted change. Actions are allowlisted, previewed, authority-checked, human-approved when required, verified, and audited. Resolto never merges or deploys code."
  }

  if (
    value.includes("connector") ||
    value.includes("slack") ||
    value.includes("stripe") ||
    value.includes("sentry")
  ) {
    return "This Cloudflare build uses fictional demo adapters for Slack intake, PostgreSQL, Stripe, Sentry, Linear, and GitHub-style workflows. No live third-party account is connected or claimed."
  }

  if (value.includes("problem") || value.includes("solve") || value.includes("do")) {
    return "Resolto turns an unclear internal software-support report into an evidence-backed explanation, a governed operational action, or a complete developer escalation."
  }

  if (value.includes("demo") || value.includes("talk") || value.includes("contact")) {
    return "You can open the fictional-data workspace now or use Contact to discuss one recurring support workflow and the controls it needs."
  }

  return "I can explain Resolto’s case flow, evidence model, governed actions, connector boundaries, demo scope, and source-outline pricing."
}

export function MarketingAssistant() {
  const panelRef = useRef<HTMLElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<number | null>(null)
  const nextMessageId = useRef(2)
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [onDark, setOnDark] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      role: "assistant",
      text: "Ask me about Resolto’s case flow, evidence, action controls, demo scope, or pricing.",
    },
  ])

  useEffect(
    () => () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
      }
    },
    [],
  )

  useEffect(() => {
    if (open || loading || messages.length > 0) {
      endRef.current?.scrollIntoView({ block: "nearest" })
    }
  }, [loading, messages.length, open])

  useEffect(() => {
    if (!open) {
      return
    }

    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node

      if (!panelRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener("pointerdown", closeOutside)
    document.addEventListener("keydown", closeOnEscape)

    return () => {
      document.removeEventListener("pointerdown", closeOutside)
      document.removeEventListener("keydown", closeOnEscape)
    }
  }, [open])

  useEffect(() => {
    let frame = 0
    const updateTone = () => {
      const trigger = triggerRef.current

      if (!trigger) {
        return
      }

      const rect = trigger.getBoundingClientRect()
      const elements = document.elementsFromPoint(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
      )
      const background = elements.find(
        (element) =>
          !element.closest("[data-assistant-ui]") && element.closest("[data-assistant-tone]"),
      )

      setOnDark(
        background?.closest("[data-assistant-tone]")?.getAttribute("data-assistant-tone") ===
          "dark",
      )
      frame = 0
    }
    const requestUpdate = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(updateTone)
      }
    }

    window.addEventListener("scroll", requestUpdate, { passive: true })
    window.addEventListener("resize", requestUpdate)
    updateTone()

    return () => {
      window.removeEventListener("scroll", requestUpdate)
      window.removeEventListener("resize", requestUpdate)
      window.cancelAnimationFrame(frame)
    }
  }, [])

  const ask = (question: string) => {
    const trimmed = question.trim()

    if (!trimmed || loading) {
      return
    }

    const id = nextMessageId.current++
    setMessages((current) => [...current, { id, role: "user", text: trimmed }])
    setInput("")
    setLoading(true)
    timerRef.current = window.setTimeout(() => {
      setMessages((current) => [
        ...current,
        { id: id + 1, role: "assistant", text: answer(trimmed) },
      ])
      setLoading(false)
    }, 520)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    ask(input)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={onDark ? "assistant-trigger is-on-dark" : "assistant-trigger"}
        aria-label={open ? "Close Resolto product assistant" : "Open Resolto product assistant"}
        aria-expanded={open}
        aria-controls="texly-assistant-panel"
        data-assistant-ui
        onClick={() => setOpen((current) => !current)}
      >
        <MessageCircle aria-hidden="true" />
        <span>Ask Resolto</span>
      </button>
      {open && (
        <aside
          ref={panelRef}
          id="texly-assistant-panel"
          className="assistant-panel"
          aria-labelledby="assistant-panel-title"
          data-assistant-ui
        >
          <div className="assistant-dialog__header">
            <span>
              <Bot aria-hidden="true" />
            </span>
            <div>
              <strong id="assistant-panel-title">Resolto assistant</strong>
              <small>Guided product answers</small>
            </div>
            <button type="button" aria-label="Close assistant" onClick={() => setOpen(false)}>
              <X aria-hidden="true" />
            </button>
          </div>
          <div className="assistant-dialog__messages" aria-live="polite">
            {messages.map((message) => (
              <p key={message.id} data-role={message.role}>
                {message.text}
              </p>
            ))}
            {loading && (
              <div className="assistant-typing" role="status" aria-label="Preparing answer">
                <span />
                <span />
                <span />
              </div>
            )}
            <div ref={endRef} />
          </div>
          {messages.length < 3 && (
            <div className="assistant-dialog__prompts">
              {prompts.map((prompt) => (
                <button key={prompt} type="button" onClick={() => ask(prompt)}>
                  {prompt}
                </button>
              ))}
            </div>
          )}
          <form className="assistant-dialog__form" onSubmit={submit}>
            <label htmlFor="assistant-question" className="sr-only">
              Ask a question about Resolto
            </label>
            <input
              id="assistant-question"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask about Resolto"
              maxLength={240}
            />
            <button type="submit" disabled={!input.trim() || loading} aria-label="Send question">
              <ArrowUp aria-hidden="true" />
            </button>
          </form>
          <p className="assistant-dialog__notice">Product guidance, not live support.</p>
        </aside>
      )}
    </>
  )
}
